/**
 * theme.ts — direct port of app/theme.py.
 * Bloomberg-style dark palette + shared style helpers.
 * Color constants match the Python module byte-for-byte so screenshots
 * of the Python v3 stay reproducible from the TS port.
 */

// ── Palette ─────────────────────────────────────────────────────────────────
// Accent / gain / loss read from CSS variables so any component using these
// values via inline style={{ color: ORG }} tracks the active theme. Plotly
// figures need real hex values — they call readThemeColors() (see store.ts)
// to get the computed colors at figure-build time.
//
// Non-theme colors (BG/PNL/CARD/BLU/YEL/PUR/CYN/PNK/TXT/MUT) stay literal
// so indicators (Bollinger blue, MACD cyan, Donchian purple) keep their
// identity across themes — only accent and direction colors flex.
export const BG   = '#090909';
export const PNL  = '#111111';
export const CARD = '#181818';
export const ORG  = 'var(--accent)';
export const GRN  = 'var(--gain)';
export const RED  = 'var(--loss)';
export const BLU  = '#33aaff';
export const YEL  = '#ffee33';
export const PUR  = '#cc66ff';
export const CYN  = '#00ffcc';
export const PNK  = '#ff66aa';
export const TXT  = '#e0e0e0';
export const MUT  = '#555555';
export const FONT = "Consolas,'Courier New',monospace";

// ── Shared style dicts (Python IS / OS / TKW) ──────────────────────────────

import type { CSSProperties } from 'react';

export const IS: CSSProperties = {
  background: CARD,
  color:      TXT,
  fontFamily: FONT,
  fontSize:   '12px',
  border:     '1px solid #2a2a2a',
};

export const OS: CSSProperties = { ...IS, border: `1px solid ${ORG}` };

// Table cell style baseline (used by screener / portfolio / options chain tables)
export const TBL_HEADER: CSSProperties = {
  backgroundColor: '#1a1a1a',
  color:           ORG,
  fontWeight:      'bold',
  border:          '1px solid #222',
  fontFamily:      FONT,
  fontSize:        '10px',
  letterSpacing:   '1px',
};

export const TBL_CELL: CSSProperties = {
  backgroundColor: PNL,
  color:           TXT,
  border:          '1px solid #1a1a1a',
  fontFamily:      FONT,
  fontSize:        '11px',
  padding:         '4px 8px',
};

/**
 * Inline Bloomberg-style label+value badge — port of theme.bdg().
 * Usage: <Badge label="LAST" value="$203.10" /> or with color override.
 */
export interface BadgeProps { label: string; value: string; color?: string }
